import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import api, { formatRM } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { usePageTitle } from "@/contexts/PageTitlesContext";
import ReminderBanner from "@/components/ReminderBanner";
import { CheckCircle2, Clock3, XCircle, ExternalLink } from "lucide-react";

function statusStyle(status) {
  switch (status) {
    case "confirmed":
      return { icon: CheckCircle2, cls: "text-[#00868e] bg-[#00868e]/10" };
    case "rejected":
    case "cancelled":
      return { icon: XCircle, cls: "text-red-700 bg-red-50" };
    default:
      return { icon: Clock3, cls: "text-amber-700 bg-amber-50" };
  }
}

export default function Dashboard() {
  const { t, i18n } = useTranslation();
  const zh = !i18n.language?.startsWith("en");
  const { user } = useAuth();
  const pt = usePageTitle("dashboard", { title_zh: t("nav.dashboard"), title_en: t("nav.dashboard"), subtitle_zh: "member", subtitle_en: "member" });
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/orders")
      .then((r) => setOrders(r.data))
      .catch(() => setOrders([]))
      .finally(() => setLoading(false));
  }, []);

  if (!user) return null;

  return (
    <div className="mx-auto max-w-5xl px-6 lg:px-10 py-16" data-testid="dashboard-page">
      <div className="text-xs uppercase tracking-[0.3em] text-[#00868e] font-semibold" data-testid="dashboard-subtitle">
        {zh ? (pt.subtitle_zh || "member") : (pt.subtitle_en || "member")}
      </div>
      <h1 className="mt-3 font-serif-cn text-4xl text-[#003645] font-semibold tracking-tight" data-testid="dashboard-title">
        {zh ? pt.title_zh : pt.title_en}
      </h1>
      <div className="mt-2 text-[#4a6e77]">
        {user.name} · {user.email}
      </div>

      {/* Points balance + agent-hub link */}
      <div className="mt-6 grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
        <Link to="/points" className="rounded-lg border border-[#e5ebeb] bg-white p-5 hover:border-[#00868e] transition-colors duration-200" data-testid="points-card">
          <div className="text-xs uppercase tracking-[0.3em] text-[#00868e] font-semibold">{t("points.balance")}</div>
          <div className="mt-2 text-2xl font-semibold text-[#005870]">RM {(Number(user.points_balance || 0)).toFixed(2)}</div>
          <div className="mt-1 text-xs text-[#4a6e77]">{t("points.history")} →</div>
        </Link>
        <Link to="/booking" className="rounded-lg border border-[#e5ebeb] bg-white p-5 hover:border-[#00868e] transition-colors duration-200" data-testid="dash-booking-card">
          <div className="text-xs uppercase tracking-[0.3em] text-[#00868e] font-semibold">{t("booking.title")}</div>
          <div className="mt-2 font-serif-cn text-lg text-[#003645] font-semibold">{t("booking.subtitle")}</div>
          <div className="mt-1 text-xs text-[#4a6e77]">{t("booking.myBookings")} →</div>
        </Link>
        <Link to="/reports" className="rounded-lg border border-[#e5ebeb] bg-white p-5 hover:border-[#00868e] transition-colors duration-200" data-testid="dash-reports-card">
          <div className="text-xs uppercase tracking-[0.3em] text-[#00868e] font-semibold">{t("reports.title")}</div>
          <div className="mt-2 font-serif-cn text-lg text-[#003645] font-semibold">{t("reports.subtitle")}</div>
          <div className="mt-1 text-xs text-[#4a6e77]">{t("reports.myRequests")} →</div>
        </Link>
        {user.role === "agent" && user.agent_status === "approved" && (
          <Link to="/agent" className="rounded-lg border border-[#005870]/25 bg-[#005870] text-white p-5 hover:bg-[#003645] transition-colors duration-200" data-testid="agent-hub-link">
            <div className="text-xs uppercase tracking-[0.3em] text-white/70 font-semibold">{t("nav.agentCenter")}</div>
            <div className="mt-2 text-2xl font-semibold">{t("agentHub.title")}</div>
            <div className="mt-1 text-xs text-white/70">{t("agentHub.withdrawable")} →</div>
          </Link>
        )}
      </div>

      {user.role === "agent" && user.agent_status === "pending" && (
        <div className="mt-6 rounded-md border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800" data-testid="dashboard-agent-pending">
          {t("auth.agentPending")}
        </div>
      )}

      {!user.profile_complete && (
        <div className="mt-8">
          <ReminderBanner />
        </div>
      )}

      <section className="mt-12">
        <h2 className="font-serif-cn text-2xl text-[#003645] font-semibold">
          {t("order.history")}
        </h2>
        <div className="mt-6">
          {loading ? (
            <div className="text-[#4a6e77]">{t("common.loading")}</div>
          ) : orders.length === 0 ? (
            <div className="rounded-lg border border-[#e5ebeb] bg-white p-10 text-center text-[#4a6e77]">
              {t("common.empty")}
              <div className="mt-4">
                <Link to="/products" className="text-[#005870] hover:text-[#00868e] font-medium">
                  {t("cart.continueShopping")}
                </Link>
              </div>
            </div>
          ) : (
            <ul className="space-y-3" data-testid="dashboard-orders">
              {orders.map((o) => {
                const S = statusStyle(o.status);
                const Icon = S.icon;
                return (
                  <li
                    key={o.id}
                    className="rounded-md border border-[#e5ebeb] bg-white p-5 flex flex-col sm:flex-row sm:items-center gap-3"
                    data-testid={`order-row-${o.id}`}
                  >
                    <div className="flex-1 min-w-0">
                      <div className="font-medium text-[#003645]">#{o.order_no}</div>
                      <div className="text-xs text-[#4a6e77] mt-1">
                        {new Date(o.created_at).toLocaleString()} · {o.items.length} item(s)
                      </div>
                    </div>
                    <div className="font-semibold text-[#005870]">{formatRM(o.total)}</div>
                    <div className={`inline-flex items-center gap-1.5 text-xs px-2.5 py-1 rounded-full ${S.cls}`}>
                      <Icon size={12} /> {t(`order.statuses.${o.status}`)}
                    </div>
                    <Link
                      to={`/orders/${o.id}`}
                      className="text-[#005870] hover:text-[#00868e] font-medium text-sm inline-flex items-center gap-1"
                      data-testid={`view-order-${o.id}`}
                    >
                      {t("common.view")} <ExternalLink size={12} />
                    </Link>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      </section>
    </div>
  );
}
